import moment from 'moment';

/** Result of clamping a Mon-Sun week to a user-selected date range. */
interface ClampedWeek {
  displayStart: moment.Moment;
  displayEnd: moment.Moment;
  sameDay: boolean;
  crossesYear: boolean;
}

/**
 * Clamp a Monday-anchored week to the selected reporting range so that the
 * label only shows dates the user actually asked about. Aggregation counts
 * upstream are unchanged; this is purely a display concern.
 */
function clampWeek(
  weekStart: string,
  rangeStart?: string,
  rangeEnd?: string
): ClampedWeek | null {
  const monday = moment(weekStart, 'YYYY-MM-DD', true);
  if (!monday.isValid()) return null;
  const sunday = monday.clone().add(6, 'days');

  const displayStart =
    rangeStart && rangeStart > monday.format('YYYY-MM-DD')
      ? moment(rangeStart, 'YYYY-MM-DD')
      : monday.clone();
  const displayEnd =
    rangeEnd && rangeEnd < sunday.format('YYYY-MM-DD')
      ? moment(rangeEnd, 'YYYY-MM-DD')
      : sunday.clone();

  return {
    displayStart,
    displayEnd,
    sameDay: displayStart.isSame(displayEnd, 'day'),
    crossesYear: displayStart.year() !== displayEnd.year(),
  };
}

/**
 * Compact weekly label, used in table columns and metric subtitles:
 *   full week:      "Jul 6 - Jul 12"
 *   single day:     "Jul 6"
 *   crosses years:  "Dec 29, 25 - Jan 4, 26"
 */
export function formatWeeklyLabelCompact(
  weekStart: string,
  rangeStart?: string,
  rangeEnd?: string
): string {
  const clamped = clampWeek(weekStart, rangeStart, rangeEnd);
  if (!clamped) return weekStart;
  const { displayStart, displayEnd, sameDay, crossesYear } = clamped;
  if (sameDay) return displayStart.format('MMM D');
  if (crossesYear) {
    return `${displayStart.format('MMM D, YY')} - ${displayEnd.format('MMM D, YY')}`;
  }
  return `${displayStart.format('MMM D')} - ${displayEnd.format('MMM D')}`;
}

/**
 * Full weekly label with year, used on the chart x-axis:
 *   full week:  "Jul 6, 2026 - Jul 12, 2026"
 *   single day: "Jul 6, 2026"
 */
export function formatWeeklyLabelFull(
  weekStart: string,
  rangeStart?: string,
  rangeEnd?: string
): string {
  const clamped = clampWeek(weekStart, rangeStart, rangeEnd);
  if (!clamped) return weekStart;
  const { displayStart, displayEnd, sameDay } = clamped;
  if (sameDay) return displayStart.format('MMM D, YYYY');
  return `${displayStart.format('MMM D, YYYY')} - ${displayEnd.format('MMM D, YYYY')}`;
}

/**
 * Two-digit-year weekly label for the chart hover tooltip:
 *   full week:  "6 Jul 26 to 12 Jul 26"
 *   single day: "6 Jul 26"
 */
export function formatWeeklyLabelTooltip(
  weekStart: string,
  rangeStart?: string,
  rangeEnd?: string
): string {
  const clamped = clampWeek(weekStart, rangeStart, rangeEnd);
  if (!clamped) return weekStart;
  const { displayStart, displayEnd, sameDay } = clamped;
  if (sameDay) return displayStart.format('D MMM YY');
  return `${displayStart.format('D MMM YY')} to ${displayEnd.format('D MMM YY')}`;
}
